import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';
import { getAllArticles } from '../knowledge/knowledgeArticles';
import { getKnowledgeArticleUrlSync } from '../knowledge/slugMapping';
import {
  blogPostToContentItem,
  knowledgeArticleToContentItem,
  findTopRelevantContent,
  KNOWLEDGE_ARTICLE_KEYWORDS,
} from '../utils/contentRelevance';

interface RelatedKnowledgeProps {
  blogPost: {
    slug: string;
    title: string;
    description: string;
    keywords?: string[];
    tags?: string[];
    category?: string;
  };
  locale: string;
  maxItems?: number;
}

/**
 * RelatedKnowledge Component
 *
 * Displays 2-3 relevant Knowledge Articles at the bottom of Blog Posts.
 * Uses the content relevance algorithm to intelligently match articles
 * based on title, keywords, tags, and description similarity.
 *
 * Features:
 * - Automatic relevance matching
 * - Localized titles and slugs
 * - SSR-compatible (no lazy loading)
 * - Canonical-correct URLs
 */
export const RelatedKnowledge: React.FC<RelatedKnowledgeProps> = ({
  blogPost,
  locale,
  maxItems = 3,
}) => {
  // Get all knowledge articles
  const knowledgeArticles = useMemo(() => getAllArticles(locale), [locale]);

  // Find most relevant knowledge articles
  const relatedArticles = useMemo(() => {
    // Convert blog post to ContentItem
    const sourceContent = blogPostToContentItem(blogPost);

    // Convert knowledge articles to ContentItems with predefined keywords
    const candidateContent = knowledgeArticles.map(article =>
      knowledgeArticleToContentItem(
        article,
        KNOWLEDGE_ARTICLE_KEYWORDS[article.slug] || []
      )
    );

    // Find top relevant articles
    const relevantScores = findTopRelevantContent(sourceContent, candidateContent, {
      limit: maxItems,
      minScore: 8, // Lower threshold for broader matching
    });

    // Map back to full article objects with scores
    return relevantScores
      .map(score => {
        const article = knowledgeArticles.find(a => a.slug === score.slug);
        return article ? { article, score: score.score } : null;
      })
      .filter(Boolean) as Array<{ article: any; score: number }>;
  }, [blogPost, knowledgeArticles, maxItems]);

  // Don't render if no related articles found
  if (relatedArticles.length === 0) {
    return null;
  }

  // Get localized text
  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      heading: {
        en: 'Related Knowledge Articles',
        de: 'Verwandte Wissensartikel',
        'pt-BR': 'Artigos de Conhecimento Relacionados',
        'ms-MY': 'Artikel Pengetahuan Berkaitan',
        es: 'Artículos de Conocimiento Relacionados',
        fr: 'Articles de Connaissance Connexes',
        ja: '関連ナレッジ記事',
        id: 'Artikel Pengetahuan Terkait',
      },
      learnMore: {
        en: 'Learn more',
        de: 'Mehr erfahren',
        'pt-BR': 'Saiba mais',
        'ms-MY': 'Ketahui lebih lanjut',
        es: 'Más información',
        fr: 'En savoir plus',
        ja: '詳しく見る',
        id: 'Pelajari lebih lanjut',
      },
    };
    return translations[key]?.[locale] || translations[key]?.['en'] || '';
  };

  return (
    <section className="mt-16 pt-8 border-t border-neutral-200 dark:border-charcoal-700">
      {/* Section Heading */}
      <div className="flex items-center gap-3 mb-8">
        <BookOpen className="w-6 h-6 text-accent-600 dark:text-accent-400" />
        <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white">
          {getText('heading')}
        </h2>
      </div>

      {/* Knowledge Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {relatedArticles.map(({ article }) => {
          // Get localized URL using synchronous function (SSR-compatible)
          const articleUrl = getKnowledgeArticleUrlSync(article.slug, locale);

          return (
            <Link
              key={article.slug}
              to={articleUrl}
              className="group bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-purple-200 dark:border-purple-700/50 hover:border-purple-400 dark:hover:border-purple-500"
            >
              {/* Icon Header */}
              <div className="p-5 pb-0">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-charcoal-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-2">
                  {article.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-3">
                  {article.metaDescription}
                </p>
              </div>

              {/* Learn More Link - Unique anchor per article */}
              <div className="px-5 pb-5">
                <span className="flex items-center gap-2 text-purple-600 dark:text-purple-400 text-sm font-medium group-hover:gap-3 transition-all">
                  <span>{getText('learnMore')}: {article.title.length > 20 ? article.title.substring(0, 20) + '...' : article.title}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default RelatedKnowledge;
